import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { memo, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { pick, equals, pipe, pluck } from "ramda";
import { Flex, TertiarySmall } from "@base-ui";
import GraphFocusLabels from "@dataVisualizations/graphFocusLabels/GraphFocusLabels";
import OrganizationsList from "@dataVisualizations/organizationslist";
import { getChildOrganizations } from "@webapi/api";
import { useMeasurementsWithMetadata } from "@webapi/hooks/useMeasurementsWithMetadata";
import { useAuthContext } from "@authentication/context";
import { addVolatileUnits, copyUnitsIntoVolatileList, selectUnitList, getVolatileId, removeUnitsFromSelectedList, useUnitListsContext } from "@unitLists";
import { sortOrganizationIdsByPeriodValue } from "utils/sort";
import { extractValue } from "utils/measurement";
import { measurementsParams, adjustQueryToMeasurementsWithMetadata } from "utils/measurementsWithMetadata";
import { usePrevious } from "hooks";
import { Chart, OrganizationsHolder, GraphContainer } from "./timeline.styles";
import TimelineGraph from "./TimelineGraph";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default /*#__PURE__*/memo(function Timeline(_ref) {
  var _adjustedQuery$sort;
  var query = _ref.query,
    setQuery = _ref.setQuery,
    measure = _ref.measure,
    renderLatestExisting = _ref.renderLatestExisting,
    disableGraphInteractions = _ref.disableGraphInteractions,
    lookupOrganization = _ref.lookupOrganization,
    disableUnitsInteraction = _ref.disableUnitsInteraction;
  var _useAuthContext = useAuthContext(),
    getUser = _useAuthContext.getUser;
  var authTicket = getUser().ticket;
  var _useMeasurementsWithM = useMeasurementsWithMetadata(measurementsParams(query)),
    measurementsWithMetadata = _useMeasurementsWithM.measurementsWithMetadata,
    loading = _useMeasurementsWithM.loading;
  var measureMeasurementsWithMetadata = useMemo(function () {
    return measurementsWithMetadata.get(measure.measureId) || [];
  }, [measurementsWithMetadata, measure.measureId]);
  var adjustedQuery = useMemo(function () {
    return renderLatestExisting ? adjustQueryToMeasurementsWithMetadata(query, measureMeasurementsWithMetadata) : query;
  }, [renderLatestExisting, query, measureMeasurementsWithMetadata]);
  var _useUnitListsContext = useUnitListsContext(),
    setUnitLists = _useUnitListsContext.setUnitLists;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    focusId = _useState2[0],
    setFocusId = _useState2[1];
  var prevFocusId = usePrevious(focusId);
  var containerRef = useRef();
  var drilldown = useCallback(function (measureId, organizationId) {
    return getChildOrganizations(measureId, organizationId, authTicket).then(function (result) {
      return setUnitLists(pipe(function (_unitLists) {
        return copyUnitsIntoVolatileList(_unitLists, pluck("id", result).toObjectEntries("id"));
      }, function (_unitLists) {
        return selectUnitList(_unitLists, getVolatileId(_unitLists));
      }));
    });
  }, [setUnitLists, authTicket]);
  var translatedMeasureUnit = (measure === null || measure === void 0 ? void 0 : measure.measureUnit) === "%" ? "Procent" : measure === null || measure === void 0 ? void 0 : measure.measureUnit;
  useEffect(function () {
    setFocusId(null);
  }, [adjustedQuery]);
  return /*#__PURE__*/_jsxs(Flex.Column, {
    children: [/*#__PURE__*/_jsx(GraphFocusLabels, {
      focusId: focusId,
      setFocusId: setFocusId,
      measure: measure,
      measurementsWithMetadata: measureMeasurementsWithMetadata,
      targetMeasurements: measure === null || measure === void 0 ? void 0 : measure.targetMeasurements,
      graphSelected: adjustedQuery.graphSelected
    }), /*#__PURE__*/_jsxs(Chart, {
      className: "chart",
      children: [/*#__PURE__*/_jsx(OrganizationsHolder, {
        children: /*#__PURE__*/_jsx(OrganizationsList, {
          query: adjustedQuery,
          setQuery: setQuery,
          measure: measure,
          lookupOrganization: lookupOrganization,
          measurementsWithMetadata: measureMeasurementsWithMetadata,
          drilldown: drilldown,
          focusId: focusId,
          setFocusId: setFocusId,
          disableUnitsInteraction: disableUnitsInteraction,
          extractValueFunc: extractValue("-"),
          sortFunc: sortOrganizationIdsByPeriodValue(lookupOrganization, ((_adjustedQuery$sort = adjustedQuery.sort) === null || _adjustedQuery$sort === void 0 ? void 0 : _adjustedQuery$sort[measure.measureId]) || adjustedQuery.sort, adjustedQuery.periodType, adjustedQuery.dateTo, adjustedQuery.units),
          addOrganization: function addOrganization(id) {
            return setUnitLists(function (old) {
              return addVolatileUnits(old, [id]);
            });
          },
          removeOrganization: function removeOrganization(id) {
            return setUnitLists(function (old) {
              return removeUnitsFromSelectedList(old, [id]);
            });
          }
        })
      }), /*#__PURE__*/_jsxs(GraphContainer, {
        ref: containerRef,
        children: [/*#__PURE__*/_jsxs(TertiarySmall, {
          className: "tertiary-small",
          style: {
            margin: 0,
            textAlign: "right"
          },
          children: ["M\xE5ttenhet: ", translatedMeasureUnit]
        }), /*#__PURE__*/_jsx(TimelineGraph, {
          query: adjustedQuery,
          measure: measure,
          measurementsWithMetadata: measureMeasurementsWithMetadata,
          containerRef: containerRef,
          loading: loading,
          lookupOrganization: lookupOrganization,
          changeToSingleDateAndBarchart: function changeToSingleDateAndBarchart(dateTo) {
            return setQuery({
              graphSelected: "barchart",
              dateTo: dateTo
            });
          },
          disableGraphInteractions: disableGraphInteractions,
          focusId: focusId,
          prevFocusId: prevFocusId,
          setFocusId: setFocusId
        })]
      })]
    })]
  });
}, function (prev, curr) {
  return equals(pick(["query", "loading", "measure", "measurementsWithMetadata", "lookupOrganization"], prev), pick(["query", "loading", "measure", "measurementsWithMetadata", "lookupOrganization"], curr));
});